import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as BatteryIcon } from '../../../../../assets/icons/battery-icon.svg';
import { ReactComponent as BlackCarIcon } from '../../../../../assets/icons/black-car-icon.svg';
import { ReactComponent as CalendarIcon } from '../../../../../assets/icons/calender.svg';
import { ReactComponent as CarIcon } from '../../../../../assets/icons/car.svg';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/clock-icon.svg';
import { ReactComponent as CopyIcon } from '../../../../../assets/icons/copy.svg';
import { ReactComponent as EmailIcon } from '../../../../../assets/icons/email-icon.svg';
import { ReactComponent as FlashIcon } from '../../../../../assets/icons/flash-icon.svg';
import { ReactComponent as ForceStopIcon } from '../../../../../assets/icons/force-stop-icon.svg';
import { ReactComponent as LocationIcon } from '../../../../../assets/icons/location-icon.svg';
import { ReactComponent as NoActiveIcon } from '../../../../../assets/icons/no-active-sessions.svg';
import { ReactComponent as PlugIcon } from '../../../../../assets/icons/plug-black.svg';
import { ReactComponent as StopFillIcon } from '../../../../../assets/icons/stop-fill-icon.svg';
import { useGoogleMaps } from '../../../../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../../shared-components/button/Button';
import ConvertUTCtoDateTime from '../../../../../shared-functions/ConvertUTCtoDateTime';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../../utils/api/end-consumer-v2';
import emspSessionApi from '../../../../../utils/api/v2/emsp-session'

interface ActiveSessionProperties {
  dataList?: any;
  // eslint-disable-next-line react/no-unused-prop-types
  consumerType?: string;
  isSplitView?: boolean;
  splitView?: boolean;
  isTwoColumn?: boolean;
  layout?: 'split' | 'single';
}

/**
 * Convert lat/lng strings to valid Google map position
 * @param {string} [lat] - Latitude string
 * @param {string} [lng] - Longitude string
 * @returns {any} Google maps LatLngLiteral or null
 */
const getValidPosition = (lat?: string, lng?: string): google.maps.LatLngLiteral | null => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * Generates an SVG station marker icon encoded as a data URI.
 * @param {string} color - The hex color code for the marker.
 * @returns {string} The data URI of the generated SVG icon.
 */
const getStationMarkerIcon = (color: string): string => {
  const svg = `
    <svg width="44" height="45" viewBox="0 0 44 45"
      fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22 2.91602C14.4186 2.91602 8.25 8.81563 8.25 16.0645C8.25 19.5166 9.82352 24.1073 12.9267 29.7096C15.4189 34.2076 18.3021 38.275 19.8017 40.2988C20.0551 40.6446 20.3864 40.9259 20.7688 41.1197C21.1512 41.3136 21.5739 41.4146 22.0026 41.4146C22.4313 41.4146 22.854 41.3136 23.2363 41.1197C23.6187 40.9259 23.95 40.6446 24.2034 40.2988C25.7005 38.275 28.5863 34.2076 31.0784 29.7096C34.1765 24.1091 35.75 19.5183 35.75 16.0645C35.75 8.81563 29.5814 2.91602 22 2.91602Z"
        fill="${color}"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.2551 19.1954H28.9444C29.3221 19.1944 29.6668 19.386 29.8276 19.6883C29.9884 19.9907 29.9367 20.3475 29.6944 20.6041L19.3279 31.6541C19.1341 31.8446 18.8213 31.9 18.5603 31.7892C18.2993 31.6784 18.1517 31.4285 18.2002 31.1757L20.3058 21.7309H15.0504C14.7553 21.7289 14.4766 21.6094 14.2927 21.4052C14.1088 21.202 14.0394 20.9356 14.1033 20.6809L16.9226 9.63769C17.0184 9.24687 17.4083 8.96785 17.8609 8.96591H25.2198C25.5259 8.96202 25.8145 9.08938 25.9951 9.30715C26.1823 9.51812 26.2428 9.79616 26.1591 10.0538L23.2551 19.1954Z"
        fill="white"
      />
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Resolves the color of the station marker based on connector/session status.
 * @param {any} activeSession - The active session object
 * @returns {string} The hex color code
 */
const getSessionMarkerColor = (activeSession: any): string => {
  const statuses = activeSession?.connectorStatuses || activeSession?.station?.connectorStatuses || [];
  if (statuses.length > 0) {
    const hasAvailable = statuses.some((item: any) => item.status === 'Available');
    if (hasAvailable) return '#22c55e';

    const hasCharging = statuses.some(
      (item: any) =>
        item.status === 'Charging' ||
        item.status === 'Preparing' ||
        item.status === 'Finishing',
    );
    if (hasCharging) return '#3b82f6';

    const hasReserved = statuses.some((item: any) => item.status === 'Reserved');
    if (hasReserved) return '#FFC107';

    return '#e41010';
  }

  const sessionStatus = activeSession?.status || activeSession?.sessionStatus;
  if (sessionStatus === 'Available') return '#22c55e';
  if (sessionStatus === 'inProgress' || sessionStatus === 'Charging' || sessionStatus === 'Preparing') return '#3b82f6';
  if (sessionStatus === 'Reserved') return '#FFC107';
  if (sessionStatus === 'completed' || sessionStatus === 'Finished') return '#22c55e';

  return '#3b82f6';
};

/**
 * Active session component
 * @param {ActiveSessionProperties} properties - Component properties
 * @returns {React.ReactElement} Active session component
 */
function ActiveChargingSession(properties: ActiveSessionProperties): React.ReactElement {
  const { dataList, isSplitView, splitView, isTwoColumn, layout } = properties;
  const isSplit = Boolean(isSplitView || splitView || isTwoColumn || layout === 'split');

  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [isStop, setIsStop] = useState<boolean>(false);
  const [isForceStop, setIsForceStop] = useState<boolean>(false);
  const [activeSessionInfo, setActiveSessionInfo] = useState<any>(null);
  const [activeSessionInfoCharger, setActiveSessionInfoCharger] = useState<any>(null);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);

  const { isLoaded } = useGoogleMaps();

  const mapCenter = useMemo(() => {
    const lat =
      activeSessionInfoCharger?.latitude ||
      activeSessionInfoCharger?.chargingStationDetails?.latitude;
    const lng =
      activeSessionInfoCharger?.longitude ||
      activeSessionInfoCharger?.chargingStationDetails?.longitude;
    return getValidPosition(lat, lng);
  }, [
    activeSessionInfoCharger?.latitude,
    activeSessionInfoCharger?.longitude,
    activeSessionInfoCharger?.chargingStationDetails?.latitude,
    activeSessionInfoCharger?.chargingStationDetails?.longitude,
  ]);

  /**
   * Map container style object for GoogleMap component
   */
  const mapContainerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

  /**
   * Static map options (memoized once)
   */
  const miniMapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      draggable: false,
      zoomControl: false,
      scrollwheel: false,
    }),
    [],
  );

  /**
   * Clear live session data
   * @returns {void}
   */
  const clearLiveSessionData = (): void => {
    setActiveSessionInfo(null);
    setActiveSessionInfoCharger(null);
    setIsStop(false);
    setIsForceStop(false);
  };

  /**
   * Stop transaction.
   * @returns {Promise<void>} A promise that resolves when transaction is stopped
   */
  const handleStopTransaction = async (): Promise<void> => {
    const payload = {
      // eslint-disable-next-line camelcase
      session_id: activeSessionInfoCharger?.sessionId || activeSessionInfo?.sessionId,
    };
    try {
      setIsStop(true);
      await emspSessionApi.remoteStopTransaction(payload);
      setTimeout(() => {
        clearLiveSessionData();
      }, 5000);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Remote force stop transaction.
   * @returns {Promise<void>} A promise that resolves when transaction is force stopped
   */
  const handleForceStopTransaction = async (): Promise<void> => {
    const payload = {
      chargingPointSn:
        activeSessionInfoCharger?.chargingPointSnDetails?.chargingPointSn ||
        activeSessionInfo?.chargingPointSn ||
        activeSessionInfoCharger?.chargingPointSn,
      transactionId: activeSessionInfoCharger?.transactionId,
      userCustomId: selectedEndConsumer?.userCustomId,
      forceStopTransaction: true,
    };
    try {
      setIsForceStop(true);
      await endConsumerApi.remoteStopTransaction(payload);
      setTimeout(() => {
        clearLiveSessionData();
      }, 5000);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Populate edit form when entering edit mode
   * @returns {Promise<void>} A promise that resolves when active session is fetched
   */
  const handleActiveSession = async (): Promise<void> => {
    if (!selectedEndConsumer?.userCustomId) return;

    try {
      const response = await endConsumerApi.fetchActiveSession(selectedEndConsumer.userCustomId, {
        _id: dataList?._id,
      });
      const session = response?.data?.active || null;
      setActiveSessionInfoCharger(session);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Copy text to clipboard and trigger notification.
   * @param {string} text - The text to copy.
   * @returns {void}
   */
  const handleCopyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text);
    dispatch(
      setNotificationMessage({
        status: true,
        message: t('copiedToClipboard') || 'Copied to clipboard!',
        type: 'success',
        code: 200,
      }),
    );
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (selectedEndConsumer?.userCustomId) {
      handleActiveSession();

      interval = setInterval(() => {
        handleActiveSession();
      }, 10000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedEndConsumer?.userCustomId, endConsumerType]);

  /**
   * Render Header buttons (Stop / Force Stop)
   * @returns {any} Header buttons element
   */
  const renderHeaderButtons = (): JSX.Element => (
    <div className="header-buttons">
      <MyButton
        label={t('stop')}
        buttonType="error"
        onClickFunc={handleStopTransaction}
        loading={isStop}
        disableButton={isStop || isForceStop}
        svgIcon={<StopFillIcon />}
      />
      {activeSessionInfoCharger?.userName === 'kathirava' && (
        <MyButton
          label={t('forceStop')}
          buttonType="error"
          onClickFunc={handleForceStopTransaction}
          loading={isForceStop}
          disableButton={isStop || isForceStop}
          svgIcon={<ForceStopIcon />}
        />
      )}
    </div>
  );

  /**
   * Render Driver info card
   * @returns {any} Driver info element
   */
  const renderDriverInfoCard = (): JSX.Element => (
    <div className="driver-info-card">
      <div className="driver-image-wrapper">
        {activeSessionInfoCharger?.vehicleImageUrl ? (
          <img
            className="driver-vehicle-img"
            src={`${activeSessionInfoCharger?.vehicleImageUrl}`}
            alt="vehicle"
          />
        ) : (
          <BlackCarIcon className="driver-vehicle-img default" />
        )}
      </div>
      <div className="driver-details">
        <p className="driver-name">
          {activeSessionInfoCharger?.userName ||
            `${activeSessionInfoCharger?.driver?.firstName || ''} ${activeSessionInfoCharger?.driver?.lastName || ''}`.trim() ||
            '--'}
        </p>
        <p className="driver-id">{activeSessionInfoCharger?.userCustomId || '--'}</p>
        <div className="driver-contact-row">
          <span className="driver-email">
            <EmailIcon className="email-svg" />
            {activeSessionInfoCharger?.driver?.email || '--'}
          </span>
          <span className="live-status-badge">
            <span className="status-dot green" />
            {activeSessionInfoCharger?.status === 'inProgress'
              ? 'In Progress'
              : activeSessionInfoCharger?.status || '--'}
          </span>
        </div>
      </div>
    </div>
  );

  /**
   * Render Vehicle details card
   * @returns {any} Vehicle details element
   */
  const renderVehicleDetailsCard = (): JSX.Element => (
    <div className="active-charging-session-content">
      <div className="vehicle-info-section">
        <div className="vehicle-details-card">
          <div className="vehicle-icon-circle">
            <CarIcon className="car-svg" />
          </div>
          <div className="vehicle-info-right">
            <p className="vehicle-name-title">
              {activeSessionInfoCharger?.vehicle?.manufacturer || '--'}{' '}
              {activeSessionInfoCharger?.vehicle?.model || ''}
            </p>
            <div className="vehicle-specs-row">
              <span className="spec-item">
                <CalendarIcon className="spec-icon" />
                {activeSessionInfoCharger?.vehicle?.manufacturerYear || '--'}
              </span>
              <div className="vertical-line-divider" />
              <span className="spec-item">
                <BatteryIcon className="spec-icon" />
                {activeSessionInfoCharger?.vehicle?.batteryCapacity
                  ? `${activeSessionInfoCharger.vehicle.batteryCapacity} kWh`
                  : '--'}
              </span>
              <div className="vertical-line-divider" />
              <span className="spec-item">
                <PlugIcon className="spec-icon" />
                {activeSessionInfoCharger?.vehicle?.connectorType || '--'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * Render Session Timestamps bar
   * @returns {any} Session timestamps element
   */
  const renderTimestampsBar = (): JSX.Element => (
    <div className="session-timestamps-bar">
      <div className="timestamp-item">
        <div className="timestamp-icon-wrap energy">
          <FlashIcon className="timestamp-icon" />
        </div>
        <div className="timestamp-details">
          <span className="timestamp-label">{t('energy', 'Energy (kWh)')}</span>
          <span className="timestamp-value energy-value">
            {activeSessionInfoCharger?.energyKwh != null
              ? `${ToFixedValue(activeSessionInfoCharger.energyKwh)} kWh`
              : '--'}
          </span>
        </div>
      </div>
      <div className="vertical-line-divider" />
      <div className="timestamp-item">
        <CalendarIcon className="timestamp-icon" />
        <div className="timestamp-details">
          <span className="timestamp-label">{t('sessionStarted', 'Session Started')}</span>
          <span className="timestamp-value">
            {activeSessionInfoCharger?.sessionStartAt
              ? ConvertUTCtoDateTime(activeSessionInfoCharger.sessionStartAt)
              : '--'}
          </span>
        </div>
      </div>
      <div className="vertical-line-divider" />
      <div className="timestamp-item">
        <ClockIcon className="timestamp-icon" />
        <div className="timestamp-details">
          <span className="timestamp-label">{t('lastUpdated', 'Last Updated')}</span>
          <span className="timestamp-value">
            {activeSessionInfoCharger?.lastUpdated
              ? ConvertUTCtoDateTime(activeSessionInfoCharger.lastUpdated)
              : '--'}
          </span>
        </div>
      </div>
    </div>
  );

  /**
   * Render Location & Charger info card
   * @returns {any} Location and charger info element
   */
  const renderLocationChargerInfoCard = (): JSX.Element => (
    <div className="location-charger-info-card">
      <div className="card-header">
        <div className="icon-wrapper">
          <LocationIcon className="location-icon" />
        </div>
        <span className="card-title">{t('locationAndCharger', 'Location & Charger')}</span>
      </div>
      <div className="card-grid">
        <div className="grid-item">
          <span className="grid-label">{t('locationId', 'Location ID')}</span>
          <span className="grid-value">{activeSessionInfoCharger?.locationId || '--'}</span>
        </div>
        <div className="vertical-line-divider" />
        <div className="grid-item">
          <span className="grid-label">{t('evseUid', 'EVSE UID')}</span>
          <span className="grid-value">{activeSessionInfoCharger?.evseUid || '--'}</span>
        </div>
        <div className="vertical-line-divider" />
        <div className="grid-item">
          <span className="grid-label">{t('connectorId', 'Connector ID')}</span>
          <span className="grid-value">{activeSessionInfoCharger?.connectorId || '--'}</span>
        </div>
      </div>
    </div>
  );

  /**
   * Render Mini Map element
   * @returns {any} Mini map element or null if maps not loaded
   */
  const renderMiniMap = (): JSX.Element | null => {
    if (!isLoaded) return null;
    return (
      <div className="mini-map">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter || { lat: 0, lng: 0 }}
          zoom={12}
          options={miniMapOptions}>
          <Marker
            position={mapCenter || { lat: 0, lng: 0 }}
            icon={{
              url: getStationMarkerIcon(
                getSessionMarkerColor(activeSessionInfoCharger || activeSessionInfo),
              ),
              scaledSize: new window.google.maps.Size(36, 36),
            }}
          />
        </GoogleMap>
      </div>
    );
  };

  /**
   * Render Active Session details grid card
   * @returns {any} Session details grid card element
   */
  const renderSessionDetailsGridCard = (): JSX.Element => (
    <div className="active-session-details-grid-card">
      <div className="grid-row">
        <div className="grid-cell">
          <div className="cell-header">
            <span className="cell-label">{t('status')}</span>
          </div>
          <div className="cell-value status-value">
            <span className="status-dot green" />
            {activeSessionInfoCharger?.status === 'inProgress'
              ? 'In Progress'
              : activeSessionInfoCharger?.status || '--'}
          </div>
        </div>

        <div className="grid-cell">
          <div className="cell-header">
            <span className="cell-label">{t('sessionId', 'Session ID')}</span>
          </div>
          <div className="cell-value session-id-value">
            <span className="session-id">{activeSessionInfoCharger?.sessionId || '--'}</span>
            {activeSessionInfoCharger?.sessionId && (
              <CopyIcon
                className="copy-btn-icon"
                onClick={() => handleCopyToClipboard(activeSessionInfoCharger.sessionId)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="horizontal-divider-line" />

      <div className="grid-row">
        <div className="grid-cell">
          <div className="cell-header">
            <span className="cell-label">{t('partyId', 'Party ID')}</span>
          </div>
          <div className="cell-value">{activeSessionInfoCharger?.partyId || '--'}</div>
        </div>

        <div className="grid-cell">
          <div className="cell-header">
            <span className="cell-label">{t('countryCode', 'Country Code')}</span>
          </div>
          <div className="cell-value">{activeSessionInfoCharger?.countryCode || '--'}</div>
        </div>
      </div>

      <div className="horizontal-divider-line" />

      <div className="grid-row">
        <div className="grid-cell">
          <div className="cell-header">
            <span className="cell-label">{t('authMethod', 'Auth Method')}</span>
          </div>
          <div className="cell-value">{activeSessionInfoCharger?.authMethod || '--'}</div>
        </div>

        <div className="grid-cell">
          <div className="cell-header">
            <span className="cell-label">{t('currency', 'Currency')}</span>
          </div>
          <div className="cell-value">{activeSessionInfoCharger?.currency || '--'}</div>
        </div>
      </div>

      <div className="horizontal-divider-line" />

      <div className="grid-row">
        <div className="grid-cell">
          <div className="cell-header">
            <span className="cell-label">{t('paymentMethod')}</span>
          </div>
          <div className="cell-value">
            {activeSessionInfoCharger?.preferredPaymentMethod || '--'}
          </div>
        </div>

        <div className="grid-cell">
          <div className="cell-header">
            <span className="cell-label">{t('authReference', 'Auth Reference')}</span>
          </div>
          <div className="cell-value">{activeSessionInfoCharger?.authReference || '—'}</div>
        </div>
      </div>
    </div>
  );

  if (!activeSessionInfoCharger) {
    return (
      <div className="active-charging-session-wrapper" dir={i18n.dir()}>
        <div className="no-active-session">
          <NoActiveIcon />
          <p>{t('noActiveSession')}</p>
        </div>
      </div>
    );
  }

  if (isSplit) {
    return (
      <div className="active-charging-session-wrapper" dir={i18n.dir()}>
        <div className="active-charging-session-split-container">
          {/* 🔹 LEFT COLUMN */}
          <div className="split-column-left">
            {renderHeaderButtons()}
            {renderDriverInfoCard()}
            {renderVehicleDetailsCard()}
            {renderTimestampsBar()}
            {renderLocationChargerInfoCard()}
          </div>

          {/* 🔹 RIGHT COLUMN */}
          <div className="split-column-right">
            {renderMiniMap()}
            {renderSessionDetailsGridCard()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="active-charging-session-wrapper" dir={i18n.dir()}>
      <div className="active-charging-session-straight-container">
        {/* 🔹 STRAIGHT LINE (SINGLE COLUMN) FLOW */}
        {renderHeaderButtons()}
        {renderDriverInfoCard()}
        {renderVehicleDetailsCard()}
        {renderTimestampsBar()}
        {renderLocationChargerInfoCard()}
        {renderMiniMap()}
        {renderSessionDetailsGridCard()}
      </div>
    </div>
  );
}

ActiveChargingSession.defaultProps = {
  dataList: [],
  consumerType: '',
  isSplitView: false,
  splitView: false,
  isTwoColumn: false,
  layout: 'single',
};

export default ActiveChargingSession;
